"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Cpu, Boxes, Brain, Code, FlaskConical, Bug, Zap, ArrowRight, Globe, CircleCheck as CheckCircle2, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
export default function TeachingPhilosophy() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    const getInitialLang = (): "vi" | "en" => {
      if (typeof window !== "undefined") {
        const saved = localStorage.getItem("roboto_lang");
        if (saved === "en" || saved === "vi") return saved;
      }
      return "vi";
    };
    setLang(getInitialLang());
    const handleLangChange = (e: Event) => {
      const customEv = e as CustomEvent<{
        lang?: "vi" | "en";
      }>;
      if (customEv.detail?.lang) {
        setLang(customEv.detail.lang);
      } else {
        const saved = localStorage.getItem("roboto_lang");
        if (saved === "en" || saved === "vi") setLang(saved);
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
    };
  }, []);
  const toggleLanguage = (newLang: "vi" | "en") => {
    setLang(newLang);
    if (typeof window !== "undefined") {
      localStorage.setItem("roboto_lang", newLang);
      window.dispatchEvent(new CustomEvent("roboto-lang-change", {
        detail: {
          lang: newLang
        }
      }));
    }
  };
  const steps = [{
    id: "01",
    code: "BUILD",
    titleVi: "XÂY DỰNG",
    titleEn: "BUILD",
    descVi: "Lắp ráp mô hình & cấu trúc vật lý",
    descEn: "Assemble physical models & mechanisms",
    icon: Boxes,
    accent: "border-primary/40 text-primary"
  }, {
    id: "02",
    code: "THINK",
    titleVi: "TƯ DUY",
    titleEn: "THINK",
    descVi: "Phân tích cơ chế & nguyên lý kỹ thuật",
    descEn: "Analyze mechanics & engineering principles",
    icon: Brain,
    accent: "border-secondary/60 text-secondary-foreground"
  }, {
    id: "03",
    code: "PROGRAM",
    titleVi: "LẬP TRÌNH",
    titleEn: "PROGRAM",
    descVi: "Xây dựng thuật toán & mã điều khiển",
    descEn: "Develop logic algorithms & control code",
    icon: Code,
    accent: "border-primary/40 text-primary"
  }, {
    id: "04",
    code: "TEST",
    titleVi: "THỬ NGHIỆM",
    titleEn: "TEST",
    descVi: "Vận hành mô hình trong thực tế",
    descEn: "Execute model in real test conditions",
    icon: FlaskConical,
    accent: "border-secondary/60 text-secondary-foreground"
  }, {
    id: "05",
    code: "DEBUG",
    titleVi: "SỬA LỖI",
    titleEn: "DEBUG",
    descVi: "Khoanh vùng, phân tích & khắc phục sự cố",
    descEn: "Identify, analyze & resolve errors",
    icon: Bug,
    accent: "border-primary/40 text-primary"
  }, {
    id: "06",
    code: "IMPROVE",
    titleVi: "CẢI TIẾN",
    titleEn: "IMPROVE",
    descVi: "Tối ưu hóa & giải quyết bài toán đặt ra",
    descEn: "Optimize performance & solve challenges",
    icon: Zap,
    accent: "border-secondary/60 text-secondary-foreground"
  }];
  return <section id="teachingphilosophy" data-nav="light" className="relative bg-background text-foreground py-16 lg:py-24 overflow-hidden border-b border-border">
      {/* Technical Blueprint Grid Backdrop */}
      <div className="absolute inset-0 pointer-events-none opacity-20" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(var(--border)_1px,transparent_1px)] [background-size:24px_24px]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:96px_96px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Language switch bar & Header with Classroom Photo */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center mb-12">
          <div className="lg:col-span-7 space-y-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <Badge variant="outline" className="bg-muted/80 text-muted-foreground border-border px-3 py-1 font-mono text-xs tracking-wider uppercase rounded-md flex items-center gap-1.5">
                <Cpu className="size-3.5 text-primary" />
                {lang === "vi" ? "TRIẾT LÝ GIÁO DỤC & PHƯƠNG PHÁP" : "TEACHING PHILOSOPHY & METHODOLOGY"}
              </Badge>

              {/* Bilingual Toggle Button */}
              <div className="flex items-center gap-1 bg-card border border-border p-1 rounded-lg shadow-xs shrink-0">
                <Button variant={lang === "vi" ? "default" : "ghost"} size="sm" onClick={() => toggleLanguage("vi")} className="text-xs font-mono px-3 h-8">
                  <Globe className="size-3.5 mr-1" />
                  VIE
                </Button>
                <Button variant={lang === "en" ? "default" : "ghost"} size="sm" onClick={() => toggleLanguage("en")} className="text-xs font-mono px-3 h-8">
                  <Globe className="size-3.5 mr-1" />
                  ENG
                </Button>
              </div>
            </div>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-foreground tracking-tight leading-tight">
              {lang === "vi" ? "ROBOTO KHÔNG CHỈ DẠY TRẺ LẮP RÁP ROBOT" : "ROBOTO DOES MORE THAN TEACH ROBOT ASSEMBLY"}
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed font-default">
              {lang === "vi" ? <>
                  Robotics tại <strong className="text-foreground">ROBOTO</strong> được
                  tiếp cận như một quá trình phát triển tư duy kỹ thuật; học sinh
                  không chỉ dừng lại ở việc lắp ráp một mô hình chuyển động, mà học
                  cách từng bước:{" "}
                  <span className="text-foreground font-semibold">
                    Quan sát, Phân tích, Xây dựng, Lập trình, Thử nghiệm, Phát hiện lỗi, Điều chỉnh, và Giải quyết nhiệm vụ.
                  </span>
                </> : <>
                  Robotics at <strong className="text-foreground">ROBOTO</strong> is
                  approached as a process of developing engineering thinking;
                  students don't just build a moving model, they learn step by step
                  to{" "}
                  <span className="text-foreground font-semibold">
                    Observe, Analyze, Build, Program, Test, Debug, Adjust, and Solve.
                  </span>
                </>}
            </p>
          </div>

          {/* Authentic ROBOTO Classroom Mentoring Photograph Card */}
          <div className="lg:col-span-5 relative group">
            <div className="relative rounded-2xl border border-border bg-card p-2.5 shadow-md transition-all duration-300 hover:shadow-lg">
              <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_classroom_mentoring.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_classroom_mentoring.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_classroom_mentoring.png-wvc-sizes" alt={lang === "vi" ? "Lớp học Robotics và tương tác hướng dẫn trực tiếp tại ROBOTO" : "ROBOTO classroom robotics mentoring interaction"} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent pointer-events-none" />

                {/* Technical Overlay Badges */}
                <div className="absolute top-3 left-3 flex items-center gap-2">
                  <span className="bg-background/90 backdrop-blur-md text-foreground font-mono text-[10px] font-bold px-2.5 py-1 rounded-md border border-border shadow-xs flex items-center gap-1.5">
                    <span className="size-1.5 rounded-full bg-secondary animate-pulse" />
                    ROBOTO LAB // MENTORING
                  </span>
                </div>

                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-xs font-mono text-foreground/90 bg-background/80 backdrop-blur-md px-3 py-2 rounded-lg border border-border/80">
                  <span className="truncate font-sans font-medium text-xs">
                    {lang === "vi" ? "Hướng dẫn 1:1 trong phòng Lab kỹ thuật" : "1-on-1 Mentoring in Technical Lab"}
                  </span>
                  <span className="text-emerald-600 dark:text-emerald-400 font-mono text-[10px] font-bold shrink-0 ml-2">
                    ACTIVE_LAB
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Visual Engineering Flow Diagram */}
        <div className="my-10">
          <div className="hidden lg:flex items-center justify-between mb-4 px-2">
            <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-primary animate-pulse" />
              {lang === "vi" ? "Quy trình tư duy kỹ thuật chuẩn" : "Standard Engineering Mindset Flow"}
            </span>
            <span className="text-xs font-mono text-muted-foreground">
              ENGINEERING_FLOW // REV_2.4
            </span>
          </div>

          {/* Desktop & Tablet Horizontal Grid / Flow */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 relative">
            {steps.map((step, index) => {
            const IconComp = step.icon;
            return <motion.div key={step.id} data-index={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.35,
              delay: index * 0.06
            }} className="relative group">
                  <Card className="h-full bg-card hover:bg-card/90 border border-border shadow-sm hover:shadow-md transition-all duration-300 rounded-xl overflow-hidden flex flex-col justify-between">
                    <CardContent className="p-5 flex flex-col h-full justify-between gap-4">
                      {/* Top Header Tag */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-mono text-xs font-bold text-muted-foreground bg-muted px-2 py-0.5 rounded border border-border">
                            {step.id}
                          </span>
                          <span className="text-[10px] font-mono text-muted-foreground/70 uppercase">
                            {step.code}
                          </span>
                        </div>

                        {/* Icon & Title */}
                        <div className="flex items-center gap-2.5 mb-2">
                          <div className={`p-2 rounded-lg bg-muted border ${step.accent}`}>
                            <IconComp className="size-5" />
                          </div>
                          <h3 className="font-sans font-bold text-base text-foreground group-hover:text-primary transition-colors">
                            {lang === "vi" ? step.titleVi : step.titleEn}
                          </h3>
                        </div>

                        {/* Description */}
                        <p className="text-xs text-muted-foreground leading-snug">
                          {lang === "vi" ? step.descVi : step.descEn}
                        </p>
                      </div>

                      {/* Status indicator line */}
                      <div className="pt-3 border-t border-border/60 flex items-center justify-between text-[11px] font-mono text-muted-foreground">
                        <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                          <CheckCircle2 className="size-3" />
                          PHASE_{step.id}
                        </span>
                        {index < steps.length - 1 && <ArrowRight className="size-3.5 text-muted-foreground hidden lg:block" />}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Desktop Connecting Arrow between cards */}
                  {index < steps.length - 1 && <div className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 z-20 size-6 bg-background border border-border rounded-full items-center justify-center text-muted-foreground shadow-xs pointer-events-none" aria-hidden="true">
                      <ArrowRight className="size-3" />
                    </div>}
                </motion.div>;
          })}
          </div>
        </div>

        {/* Disclaimer Framing Note */}
        <div className="mt-8 pt-6 border-t border-border/80 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs font-default text-muted-foreground">
          <div className="flex items-start gap-2 max-w-3xl">
            <HelpCircle className="size-4 text-primary shrink-0 mt-0.5" />
            <p className="italic">
              {lang === "vi" ? "* Sơ đồ trên thể hiện triết lý và phương pháp tiếp cận tư duy kỹ thuật tại ROBOTO, không phải là khung chương trình đào tạo cố định 6 bước." : "* Note: The diagram above represents ROBOTO's teaching philosophy and engineering thinking approach, not a rigid 6-step official curriculum."}
            </p>
          </div>

          <div className="font-mono text-[11px] bg-muted px-3 py-1.5 rounded-md border border-border shrink-0">
            FRAMEWORK // METHODOLOGY_ACADEMY
          </div>
        </div>
      </div>
    </section>;
}